import { supabase } from '../lib/supabase';
import { refreshClientSessionId } from '../lib/supabase';
import { makeImageSquare, editImageWithOpenAI, processImageForOpenAI, analyzeImages, uploadOpenAIGeneratedImage } from '../lib/openai';
import { getStreetViewImage } from '../lib/google-maps';
import { uploadBase64Image } from '../lib/storage';
import { ImageStyle, PropertySearch } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { propertyTokenGenerator } from './tokenGenerator';

// Constants
const CLIENT_SESSION_ID_KEY = 'framemyhome_client_session_id';
export const MAX_RETRIES = 3;
export const RETRY_DELAY = 1000;
export const PRIMARY_WEBHOOK_URL = 'https://hook.us2.make.com/filxhrei9jjsceheityttspsnrecqilo';
export const SECONDARY_WEBHOOK_URL = 'https://hook.us2.make.com/ipu44pbh2w26bvu1464nmqjdt8rodczy';
export const DEFAULT_IMAGE_URL = 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//No_Image.png';

// Helper function to delay execution
export const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

// Helper function to analyze image and send to widget (non-blocking)
export const analyzeImageAndNotifyWidget = async (imageUrl: string, imageSource: 'upload' | 'webcam' | 'address'): Promise<void> => {
  // Fire and forget - don't block the main flow
  (async () => {
    try {
      console.log('🔍 Starting image analysis (non-blocking):', imageUrl);
      
      // Call the analyze-images Supabase function
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ imageUrl })
      });

      if (!response.ok) {
        throw new Error(`Analysis failed: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Image analysis complete:', result);

      // Send to widget if available
      if (typeof window !== 'undefined' && (window as any).feedbackWidget) {
        try {
          (window as any).feedbackWidget.sendAnalysis({
            imageUrl,
            imageSource,
            imageType: result.imageType,
            description: result.description,
            suitability: result.suitability,
            timestamp: result.timestamp
          });
          console.log('📤 Analysis sent to widget');
        } catch (widgetError) {
          console.warn('⚠️ Failed to send analysis to widget:', widgetError);
          // Continue - widget integration is optional
        }
      } else {
        console.log('ℹ️ Widget not available, skipping analysis notification');
      }

      // Trigger custom AI greeting with image description (only on larger screens)
      if (typeof window !== 'undefined' && (window as any).triggerImageUpload) {
        // Check screen size - only trigger greeting on screens >= 480px
        const isSmallScreen = window.innerWidth < 480;
        
        if (isSmallScreen) {
          console.log('📱 Small screen detected, suppressing automated AI greeting');
        } else {
          try {
            // Use the first paragraph of the description for the greeting
            const greetingText = result.description.split('\n\n')[0] || result.description;
            console.log('📝 Greeting text to send:', greetingText.substring(0, 100) + '...');
            (window as any).triggerImageUpload(greetingText);
            console.log('💬 Triggered AI greeting with image description');
          } catch (greetingError) {
            console.error('⚠️ Failed to trigger AI greeting:', greetingError);
            // Continue - greeting is optional
          }
        }
      } else {
        console.warn('⚠️ triggerImageUpload not available on window object');
        console.log('Available window properties:', Object.keys(window).filter(k => k.includes('trigger') || k.includes('widget') || k.includes('feedback')));
      }
    } catch (error) {
      console.warn('⚠️ Image analysis failed (non-blocking):', error);
      // Silently fail - don't interrupt the main flow
    }
  })();
};

// Helper function to desaturate an image by a given percentage
const desaturateImage = async (imageUrl: string, desaturationPercentage: number = 20): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    
    img.onload = () => {
      try {
        // Create canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          throw new Error('Failed to get canvas context');
        }
        
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Draw the original image
        ctx.drawImage(img, 0, 0);
        
        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Process each pixel for desaturation
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          
          // Calculate grayscale value (luminance)
          const gray = 0.299 * r + 0.587 * g + 0.114 * b;
          
          // Blend original color with grayscale based on desaturation percentage
          const factor = desaturationPercentage / 100;
          data[i] = r + factor * (gray - r);     // Red
          data[i + 1] = g + factor * (gray - g); // Green
          data[i + 2] = b + factor * (gray - b); // Blue
          // Alpha channel (data[i + 3]) remains unchanged
        }
        
        // Put the processed image data back
        ctx.putImageData(imageData, 0, 0);
        
        // Convert to base64
        const desaturatedImageUrl = canvas.toDataURL('image/jpeg', 0.95);
        resolve(desaturatedImageUrl);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image for desaturation'));
    };
    
    img.src = imageUrl;
  });
};

// Helper function to brighten an image by a given percentage
const brightenImage = async (imageUrl: string, brightnessPercentage: number = 25): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    
    img.onload = () => {
      try {
        // Create canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          throw new Error('Failed to get canvas context');
        }
        
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Draw the original image
        ctx.drawImage(img, 0, 0);
        
        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Calculate brightness adjustment factor
        const brightnessMultiplier = 1 + (brightnessPercentage / 100);
        
        // Process each pixel for brightness adjustment
        for (let i = 0; i < data.length; i += 4) {
          // Apply brightness adjustment to RGB channels
          data[i] = Math.min(255, data[i] * brightnessMultiplier);     // Red
          data[i + 1] = Math.min(255, data[i + 1] * brightnessMultiplier); // Green
          data[i + 2] = Math.min(255, data[i + 2] * brightnessMultiplier); // Blue
          // Alpha channel (data[i + 3]) remains unchanged
        }
        
        // Put the processed image data back
        ctx.putImageData(imageData, 0, 0);
        
        // Convert to base64
        const brightenedImageUrl = canvas.toDataURL('image/jpeg', 0.95);
        resolve(brightenedImageUrl);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image for brightness adjustment'));
    };
    
    img.src = imageUrl;
  });
};

// Helper function to adjust HSL lightness (like Photoshop's lightness slider)
const adjustLightness = async (imageUrl: string, lightnessAdjustment: number = 20): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    
    img.onload = () => {
      try {
        // Create canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          throw new Error('Failed to get canvas context');
        }
        
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Draw the original image
        ctx.drawImage(img, 0, 0);
        
        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Helper function to convert RGB to HSL
        const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
          r /= 255;
          g /= 255;
          b /= 255;
          
          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          let h = 0, s = 0, l = (max + min) / 2;
          
          if (max !== min) {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            
            switch (max) {
              case r: h = (g - b) / d + (g < b ? 6 : 0); break;
              case g: h = (b - r) / d + 2; break;
              case b: h = (r - g) / d + 4; break;
            }
            h /= 6;
          }
          
          return [h, s, l];
        };
        
        // Helper function to convert HSL to RGB
        const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
          const hue2rgb = (p: number, q: number, t: number): number => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1/6) return p + (q - p) * 6 * t;
            if (t < 1/2) return q;
            if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
            return p;
          };
          
          let r, g, b;
          
          if (s === 0) {
            r = g = b = l; // achromatic
          } else {
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1/3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1/3);
          }
          
          return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
        };
        
        // Process each pixel for lightness adjustment
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          
          // Convert to HSL
          const [h, s, l] = rgbToHsl(r, g, b);
          
          // Adjust lightness (similar to Photoshop's lightness slider)
          // Lightness adjustment from -100 to +100 (like Photoshop)
          const adjustmentFactor = lightnessAdjustment / 100;
          let newL = l;
          
          if (adjustmentFactor > 0) {
            // Increase lightness: blend towards white
            newL = l + (1 - l) * adjustmentFactor;
          } else {
            // Decrease lightness: blend towards black
            newL = l + l * adjustmentFactor;
          }
          
          // Clamp lightness to valid range
          newL = Math.max(0, Math.min(1, newL));
          
          // Convert back to RGB
          const [newR, newG, newB] = hslToRgb(h, s, newL);
          
          // Update pixel data
          data[i] = newR;     // Red
          data[i + 1] = newG; // Green
          data[i + 2] = newB; // Blue
          // Alpha channel (data[i + 3]) remains unchanged
        }
        
        // Put the processed image data back
        ctx.putImageData(imageData, 0, 0);
        
        // Convert to base64
        const adjustedImageUrl = canvas.toDataURL('image/jpeg', 0.95);
        resolve(adjustedImageUrl);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image for lightness adjustment'));
    };
    
    img.src = imageUrl;
  });
};

// Helper function to send data to webhook with retries
export const sendToWebhook = async (data: any, webhookUrl: string, retries = 0): Promise<Response> => {
  try {
    console.log(`Sending data to webhook: ${webhookUrl.substring(0, 20)}...`);
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      // If we haven't exceeded max retries, wait and try again
      if (retries < MAX_RETRIES) {
        await delay(RETRY_DELAY);
        return sendToWebhook(data, webhookUrl, retries + 1);
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response;
  } catch (error) {
    if (retries < MAX_RETRIES) {
      await delay(RETRY_DELAY);
      return sendToWebhook(data, webhookUrl, retries + 1);
    }
    throw error;
  }
};

// Function to extract URL from webhook response
export const extractUrlFromResponse = async (response: Response): Promise<string | null> => {
  let responseData = await response.text();
  console.log('Webhook response received:', responseData);
  
  let imageUrl: string | null = null;
  
  // Try to parse as JSON or extract URL
  try {
    // Check if it's JSON
    const jsonData = JSON.parse(responseData);
    if (jsonData.image_url) {
      imageUrl = jsonData.image_url;
    } else if (jsonData.url) {
      imageUrl = jsonData.url;
    } else if (jsonData.error) {
      throw new Error(jsonData.error);
    }
  } catch (jsonError) {
    // Check if the response is a direct URL
    const urlMatch = responseData.match(/https?:\/\/[^\s"<>]+/);
    if (urlMatch) {
      imageUrl = urlMatch[0];
    }
  }
  
  return imageUrl;
};

// Function to detect if an image is WebP from its URL or content-type
export const isWebPImage = async (url: string): Promise<boolean> => {
  try {
    // First check if URL has a .webp extension
    if (url.toLowerCase().endsWith('.webp')) {
      return true;
    }
    
    // Try to make a HEAD request to check content-type, with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
    
    try {
      const response = await fetch(url, { 
        method: 'HEAD',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // Check if the response is successful
      if (!response.ok) {
        console.warn(`HEAD request failed for ${url}: ${response.status} ${response.statusText}`);
        return false;
      }
      
      const contentType = response.headers.get('content-type');
      return contentType?.includes('webp') || false;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      
      // If the HEAD request fails, try a GET request with no-cors mode
      console.warn(`HEAD request failed for ${url}, trying alternative method: ${fetchError.message}`);
      
      // For GET requests that might fail with CORS, just check the URL extension
      const urlExtension = url.split('.').pop()?.toLowerCase();
      return urlExtension === 'webp';
    }
  } catch (error) {
    console.warn('Error checking image format:', error);
    // Return false in case of any error, allowing the app to continue
    return false;
  }
};

// Function to convert WebP to JPEG and upload to Supabase
export const convertAndUploadImage = async (imageUrl: string): Promise<string | null> => {
  try {
    console.log(`Converting image to JPEG: ${imageUrl}`);
    
    // Create a canvas element to perform the conversion
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      
      img.onload = async () => {
        try {
          // Create canvas at the image dimensions
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          
          // Draw the image on a white background
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            throw new Error('Failed to get canvas context');
          }
          
          // Fill with white background to remove transparency
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Draw the image on top
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          // Convert to JPEG format
          const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.95);
          
          // Upload the converted image to Supabase
          const filename = `web-image-${Date.now()}.jpg`;
          const publicUrl = await uploadBase64Image(jpegDataUrl, 'propertyimages', filename);
          
          console.log(`Converted and uploaded image to: ${publicUrl}`);
          resolve(publicUrl);
        } catch (error) {
          console.error('Error converting image:', error);
          reject(error);
        }
      };
      
      img.onerror = (error) => {
        console.error('Error loading image for conversion:', error);
        // Check if this might be a CORS error
        const errorMessage = error instanceof ErrorEvent && error.message 
          ? error.message 
          : 'Failed to load image';
        
        if (errorMessage.includes('CORS') || errorMessage.includes('cross-origin')) {
          reject(new Error('Image blocked by CORS policy'));
        } else {
          reject(new Error('Failed to load image - may be blocked by security policies'));
        }
      };
      
      // Add cache buster to avoid CORS issues
      img.src = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
    });
  } catch (error) {
    console.error('Error converting and uploading image:', error);
    return null;
  }
};

// Handle creation of contact sheet
export const handleCreateSheet = async (
  urls: string[],
  setIsCreatingSheet: (state: boolean) => void,
  setError: (error: string | null) => void,
  setSheetImageUrl: (url: string | null) => void,
  setShowSheetLightbox: (show: boolean) => void,
  navigateToSheetPage?: (imageUrl: string) => void
): Promise<void> => {
  setIsCreatingSheet(true);
  setError(null);
  setSheetImageUrl(null);

  try {
    if (urls.length === 0) {
      throw new Error('No images available to create a contact sheet');
    }

    // Create the final list of exactly 7 URLs in the correct order
    // 1. Street View (index 0 if available, otherwise placeholder)
    // 2-4. Zillow images (or placeholders if not enough)
    // 5-7. Web images (or placeholders if not enough)
    const finalUrls: string[] = [];
    
    // 1. Process Street View (position 1)
    if (urls.length > 0) {
      finalUrls.push(urls[0]); // First image is Street View or first available
    } else {
      finalUrls.push(DEFAULT_IMAGE_URL); // Placeholder if no images
    }
    
    // 2-4. Process Zillow images (positions 2-4)
    // Assume Zillow images start at index 1 and there may be up to 3
    let zillowCount = 0;
    for (let i = 1; i < 4; i++) {
      if (i < urls.length) {
        finalUrls.push(urls[i]);
        zillowCount++;
      } else {
        finalUrls.push(DEFAULT_IMAGE_URL); // Placeholder if not enough Zillow images
      }
    }
    
    // 5-7. Process Web images (positions 5-7)
    // Start after the Zillow images (1 Street View + number of Zillow images we added)
    let webImagesStartIndex = 1 + zillowCount;
    for (let i = 0; i < 3; i++) {
      if (webImagesStartIndex + i < urls.length) {
        finalUrls.push(urls[webImagesStartIndex + i]);
      } else {
        finalUrls.push(DEFAULT_IMAGE_URL); // Placeholder if not enough Web images
      }
    }
    
    console.log(`Prepared ${finalUrls.length} URLs for contact sheet: 
      1 Street View (or first image)
      ${zillowCount} Zillow images (positions 2-4)
      ${Math.min(3, urls.length - webImagesStartIndex)} Web images (positions 5-7)
      ${7 - finalUrls.filter(url => url !== DEFAULT_IMAGE_URL).length} placeholders`);

    // Call the Supabase function to generate the contact sheet
    console.log('Calling Supabase function for contact sheet...');
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/contact_sheet`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        urls: finalUrls,
        quality: "high" // Request high quality output
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Function error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    // For image responses, we need to get the blob and create an object URL
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    
    // If we have a navigation function, navigate to the sheet page
    if (navigateToSheetPage) {
      navigateToSheetPage(objectUrl);
      return;
    }
    
    // Otherwise use the lightbox approach
    setSheetImageUrl(objectUrl);
    setTimeout(() => setShowSheetLightbox(true), 300);

  } catch (error) {
    console.error('Error creating sheet:', error);
    setError(error instanceof Error ? error.message : 'Failed to create sheet');
  } finally {
    setIsCreatingSheet(false);
  }
};

// Upload blob contact sheet to Supabase and get a URL
export const uploadContactSheetToSupabase = async (
  blob: Blob
): Promise<string | null> => {
  try {
    console.log('Uploading contact sheet to Supabase...');
    
    // Convert blob to file
    const filename = `contact-sheet-${Date.now()}.jpg`;
    const file = new File([blob], filename, { type: 'image/jpeg' });
    
    // Upload to Supabase storage
    const { data, error } = await supabase.storage
      .from('propertyimages')
      .upload(filename, file, {
        cacheControl: '3600',
        contentType: 'image/jpeg'
      });
      
    if (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
    
    // Get public URL
    const { data: urlData } = supabase.storage
      .from('propertyimages')
      .getPublicUrl(filename);
      
    console.log('Contact sheet uploaded to:', urlData.publicUrl);
    return urlData.publicUrl;
  } catch (error) {
    console.error('Error uploading contact sheet:', error);
    return null;
  }
};

// Analyze contact sheet and determine best image
export const analyzeContactSheet = async (
  sheetUrl: string,
  numberOfImages: number = 7,
  address?: string,
  zillowMainURL?: string
): Promise<{ selectedIndex: number; reason: string; suitabilityScore?: number; confidenceLevel?: string }> => {
  try {
    console.log('Analyzing contact sheet:', sheetUrl);
    
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-sheet`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        sheetUrl,
        numberOfImages,
        address,
        zillowMainURL
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to analyze contact sheet: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Analysis result:', data);
    
    return {
      selectedIndex: data.selectedIndex,
      reason: data.reason,
      suitabilityScore: data.suitabilityScore,
      confidenceLevel: data.confidenceLevel
    };
  } catch (error) {
    console.error('Error analyzing contact sheet:', error);
    throw error;
  }
};

// Assess image suitability
export const handleAssessSuitability = async (
  imageUrls: string[],
  hasZillowImages: boolean,
  imageDetails: Array<{
    type: 'Street View' | 'Zillow' | 'Web';
    url: string;
    number?: number;
  }>,
  setIsAssessing: (state: boolean) => void,
  setError: (error: string | null) => void,
  setSelectedImage: (url: string | null) => void,
  setSelectionReason: (reason: string | null) => void,
  fallbackImageUrl: string | null = null,
  address?: string,
  zillowMainURL?: string
): Promise<{
  contactSheetUrl: string | null;
  selectedImageUrl: string | null;
  suitabilityScore?: number;
  confidenceLevel?: string;
} | null> => {
  setIsAssessing(true);
  setError(null);
  
  try {
    // Check if we have insufficient images after CORS filtering
    const totalImages = imageUrls.length;
    const webImages = imageDetails.filter(detail => detail.type === 'Web').length;
    const streetViewImages = imageDetails.filter(detail => detail.type === 'Street View').length;
    const zillowImagesCount = imageDetails.filter(detail => detail.type === 'Zillow').length;
    
    console.log(`Image assessment: ${totalImages} total images, ${webImages} web images, ${streetViewImages} street view, ${zillowImagesCount} zillow`);
    
    // Only bail out if we have very few images AND they're all problematic web images
    // Street View + Zillow is a great combination that should always proceed
    const hasReliableSources = streetViewImages > 0 || zillowImagesCount > 0;
    const shouldBailOut = totalImages <= 1 || (totalImages <= 2 && webImages > 0 && !hasReliableSources);
    
    if (shouldBailOut) {
      console.warn('⚠️ Insufficient suitable images found after CORS filtering');
      
      const reason = totalImages === 0 
        ? 'No suitable images found. Web images may be blocked by CORS policies. Confidence Level: LOW'
        : 'Very few suitable images available after filtering. Consider uploading your own image. Confidence Level: LOW';
      
      setSelectedImage(fallbackImageUrl || (imageUrls.length > 0 ? imageUrls[0] : null));
      setSelectionReason(reason);
      
      return {
        contactSheetUrl: null,
        selectedImageUrl: fallbackImageUrl || (imageUrls.length > 0 ? imageUrls[0] : null),
        suitabilityScore: 10, // Very low score
        confidenceLevel: 'LOW'
      };
    }
    
    console.log('✅ Sufficient images available for assessment - proceeding with contact sheet creation');
    
    if (imageUrls.length === 0) {
      throw new Error('No images available to analyze');
    }

    // Create the final list of exactly 7 URLs in the correct order (similar to handleCreateSheet)
    const finalUrls: string[] = [];
    
    // Add Street View (or first image)
    if (imageUrls.length > 0) {
      finalUrls.push(imageUrls[0]);
    } else {
      finalUrls.push(DEFAULT_IMAGE_URL);
    }
    
    // Find Zillow images (assuming they follow Street View)
    const zillowImages = imageDetails
      .filter(detail => detail.type === 'Zillow')
      .map(detail => detail.url);
    
    // Add up to 3 Zillow images (positions 2-4)
    for (let i = 0; i < 3; i++) {
      if (i < zillowImages.length) {
        finalUrls.push(zillowImages[i]);
      } else {
        finalUrls.push(DEFAULT_IMAGE_URL);
      }
    }
    
    // Find Web images
    const webImageUrls = imageDetails
      .filter(detail => detail.type === 'Web')
      .map(detail => detail.url);
    
    // Add up to 3 Web images (positions 5-7)
    for (let i = 0; i < 3; i++) {
      if (i < webImageUrls.length) {
        finalUrls.push(webImageUrls[i]);
      } else {
        finalUrls.push(DEFAULT_IMAGE_URL);
      }
    }
    
    console.log(`Prepared ${finalUrls.length} URLs for analysis in ordered format:
      1 Street View (or first image)
      ${Math.min(3, zillowImages.length)} Zillow images
      ${Math.min(3, webImageUrls.length)} Web images
      ${7 - finalUrls.filter(url => url !== DEFAULT_IMAGE_URL).length} placeholders`);

    // Generate the contact sheet
    console.log('Generating contact sheet for analysis...');
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/contact_sheet`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        urls: finalUrls,
        quality: "high" // Request high quality for better analysis
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to generate contact sheet: ${response.status} ${response.statusText}`);
    }

    // Get the blob
    const blob = await response.blob();
    
    // Upload the contact sheet to Supabase to get a URL
    const file = new File([blob], `contact-sheet-${Date.now()}.jpg`, { type: 'image/jpeg' });
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('propertyimages')
      .upload(`contact-sheet-${Date.now()}.jpg`, file, {
        cacheControl: '3600',
        contentType: 'image/jpeg'
      });
      
    if (uploadError) {
      throw uploadError;
    }
    
    // Get the public URL of the uploaded contact sheet
    const { data: urlData } = supabase.storage
      .from('propertyimages')
      .getPublicUrl(uploadData.path);
      
    const sheetUrl = urlData.publicUrl;
    console.log('Contact sheet uploaded:', sheetUrl);

    // Analyze the contact sheet with the new fields
    const analysis = await analyzeContactSheet(sheetUrl, finalUrls.length, address, zillowMainURL);
    
    // Get the selected image
    if (analysis.selectedIndex < 1 || analysis.selectedIndex > finalUrls.length) {
      throw new Error(`Invalid selected index: ${analysis.selectedIndex}`);
    }
    
    const selectedIndex = analysis.selectedIndex - 1;
    // We need to map the selected index back to the actual image URL
    // If it's a placeholder, we'll need to handle that
    let selectedImageUrl = finalUrls[selectedIndex];
    
    // If selected image is a placeholder, use the fallback or first real image
    if (selectedImageUrl === DEFAULT_IMAGE_URL) {
      // Find the first real image
      const firstRealImage = imageUrls.find(url => url !== DEFAULT_IMAGE_URL);
      selectedImageUrl = fallbackImageUrl || firstRealImage || DEFAULT_IMAGE_URL;
      console.log(`Selected image was a placeholder, using alternative: ${selectedImageUrl}`);
    }
    
    console.log(`Selected image ${analysis.selectedIndex} (index ${selectedIndex}): ${selectedImageUrl}`);
    
    // Create enhanced reason with scores
    let enhancedReason = analysis.reason;
    
    if (analysis.suitabilityScore !== undefined) {
      enhancedReason += `\n\nSuitability Score: ${analysis.suitabilityScore}/10`;
    }
    
    if (analysis.confidenceLevel) {
      enhancedReason += `\n\nConfidence Level: ${analysis.confidenceLevel}`;
    }
    
    // Update state
    setSelectedImage(selectedImageUrl);
    setSelectionReason(enhancedReason);

    return {
      contactSheetUrl: sheetUrl,
      selectedImageUrl: selectedImageUrl,
      suitabilityScore: analysis.suitabilityScore,
      confidenceLevel: analysis.confidenceLevel
    };
  } catch (analysisError) {
    console.error('Error analyzing images:', analysisError);
    
    // If we have a fallback image, use it
    if (fallbackImageUrl) {
      setSelectedImage(fallbackImageUrl);
      setSelectionReason('Fallback to Street View image due to analysis error');
      setError(analysisError.message);
      return {
        contactSheetUrl: null,
        selectedImageUrl: fallbackImageUrl,
        suitabilityScore: undefined,
        confidenceLevel: 'LOW'
      };
    } else if (imageUrls.length > 0) {
      // Otherwise use the first available image
      setSelectedImage(imageUrls[0]);
      setSelectionReason('Automatic selection due to analysis error');
      setError(analysisError.message);
      return {
        contactSheetUrl: null,
        selectedImageUrl: imageUrls[0],
        suitabilityScore: undefined,
        confidenceLevel: 'LOW'
      };
    } else {
      throw analysisError; // Re-throw if we can't handle the error
    }
  } finally {
    setIsAssessing(false);
  }
};

// Add function to get custom prompts
const getCustomPrompts = (mode: 'home' | 'pet' = 'home') => {
  const STORAGE_KEY = 'framemyhome_custom_prompts';
  
  const DEFAULT_HOME_PROMPTS: {[key: string]: string} = {
    watercolor: "Create a beautiful watercolor painting of this house with soft, flowing colors and architectural details. Show the house on a sunny summer day.",
    pencil: "Create a beautiful high contrast pencil drawing of this house. Use detailed architectural sketch style with fine line work.",
    oil: "Create a beautiful oil painting style illustration of this house with detailed brushwork and rich colors. Show the house on a sunny summer day."
  };
  
  const DEFAULT_PET_PROMPTS: {[key: string]: string} = {
    watercolor: "Create a beautiful watercolor portrait of this pet with soft, flowing colors and expressive details. Capture the pet's personality and charm with gentle brushstrokes.",
    pencil: "Create a beautiful high contrast pencil drawing of this pet. Use detailed sketch style with fine line work to capture the pet's fur texture, facial features, and personality.",
    oil: "Create a beautiful oil painting style portrait of this pet with detailed brushwork and rich colors. Use classic portraiture techniques to capture the pet's character and presence."
  };
  
  const DEFAULT_PROMPTS = mode === 'pet' ? DEFAULT_PET_PROMPTS : DEFAULT_HOME_PROMPTS;

  const savedPrompts = localStorage.getItem(STORAGE_KEY);
  if (savedPrompts) {
    try {
      const parsed = JSON.parse(savedPrompts);
      // If saved prompts exist, use them, otherwise fall back to defaults
      return parsed[mode] || DEFAULT_PROMPTS;
    } catch (error) {
      console.error('Error parsing saved prompts:', error);
    }
  }
  return DEFAULT_PROMPTS;
};

// Generate AI images with concurrent processing and early navigation
export const generateAIImages = async (
  selectedImage: string | null,
  propertySearchId: string | null,
  setIsGeneratingAI: (state: boolean) => void,
  setError: (error: string | null) => void,
  setGeneratedImages: (images: { watercolor: string | null; oil: string | null; pencil: string | null }) => void,
  setPropertyImage: (image: string | null) => void,
  setStyleImages: (images: Record<ImageStyle, string | null>) => void,
  singleImageMode: boolean = false,
  mode: 'home' | 'pet' = 'home'
): Promise<{
  firstImageUrl: string;
  firstImageType: 'watercolor' | 'pencil' | 'oil';
}> => {
  setIsGeneratingAI(true);
  setError(null);
  
  try {
    if (!selectedImage) {
      throw new Error('No image selected for generation');
    }

    console.log('Starting AI image generation for property:', propertySearchId);
    console.log('Single image mode:', singleImageMode);
    console.log('User-selected mode:', mode);

    // === STEP 1: AUTO-DETECT IMAGE CONTENT TYPE ===
    // Analyze the image to determine if it's actually a home, pet, or something else
    let detectedContentType: 'home' | 'pet' = mode; // Default to user's selection
    
    try {
      console.log('🔍 Auto-detecting image content type...');
      const analysisResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ imageUrl: selectedImage })
      });

      if (analysisResponse.ok) {
        const analysisResult = await analysisResponse.json();
        const detectedType = analysisResult.imageType?.toLowerCase();
        
        console.log('✅ Image analysis result:', {
          detectedType,
          userMode: mode,
          description: analysisResult.description?.substring(0, 100) + '...'
        });
        
        // Simple rule: Is it a HOUSE? If NO → use pet prompts (works for pets, people, objects, etc.)
        // Pet prompts are general-purpose and work well for any non-architectural subject
        if (detectedType === 'home') {
          // It's a house/building → use home prompts (architectural focus)
          detectedContentType = 'home';
          if (mode === 'pet') {
            console.log('⚠️ OVERRIDE: Image is a house/building. Using home (architectural) prompts.');
          }
        } else {
          // It's NOT a house (could be pet, person, object, anything else) → use pet prompts
          detectedContentType = 'pet';
          if (mode === 'home') {
            console.log('⚠️ OVERRIDE: Image is NOT a house (detected: ' + detectedType + '). Using general-purpose pet prompts.');
          }
        }
      } else {
        console.warn('⚠️ Image analysis failed, using user-selected mode:', mode);
      }
    } catch (analysisError) {
      console.warn('⚠️ Image analysis error (non-blocking):', analysisError);
      console.log('Falling back to user-selected mode:', mode);
      // Continue with user's selected mode if analysis fails
    }
    
    console.log('🎨 Final mode for prompts:', detectedContentType);

    // Process the selected image
    const processedImage = await processImageForOpenAI(selectedImage);
    
    // Get custom prompts based on DETECTED content type (not just user's mode)
    const customPrompts = getCustomPrompts(detectedContentType);
    
    // Define the styles to generate based on mode
    const styles = singleImageMode 
      ? [{ name: 'watercolor', prompt: customPrompts.watercolor }]
      : [
          { name: 'watercolor', prompt: customPrompts.watercolor },
          { name: 'pencil', prompt: customPrompts.pencil },
          { name: 'oil', prompt: customPrompts.oil }
        ];

    console.log(`Generating ${styles.length} style(s):`, styles.map(s => s.name).join(', '));

    // Start image generation (concurrent for multiple styles, single for single mode)
    const imagePromises = styles.map(async ({ name, prompt }) => {
      try {
        // Use artistic messages for each style
        let startMessage;
        if (name === 'watercolor') {
          startMessage = 'Finding my brushes';
        } else if (name === 'pencil') {
          startMessage = 'Grabbing my pencils';
        } else if (name === 'oil') {
          startMessage = 'Mixing the oil paint';
        } else {
          startMessage = `Starting ${name} generation`;
        }
        
        console.log(startMessage);
        const imageUrl = await editImageWithOpenAI(processedImage, prompt);
        console.log(`Completed ${name} generation:`, imageUrl);
        
        // Apply post-processing effects based on style
        let finalImageUrl = imageUrl;
        if (name === 'watercolor') {
          console.log('Applying 35% desaturation to watercolor image...');
          finalImageUrl = await desaturateImage(imageUrl, 35);
          console.log('Desaturation applied to watercolor image');
        } else if (name === 'pencil') {
          console.log('Applying +20 lightness adjustment to pencil image...');
          finalImageUrl = await adjustLightness(imageUrl, 20);
          console.log('Lightness adjustment applied to pencil image');
        }
        
        // Upload to Supabase storage immediately
        const filename = `${name}-${Date.now()}.jpg`;
        const uploadedUrl = await uploadBase64Image(finalImageUrl, 'propertyimages', filename);
        console.log(`Uploaded ${name} image:`, uploadedUrl);
        
        // Update the database immediately when this image is ready
        if (propertySearchId) {
          try {
            const updateData: Partial<PropertySearch> = {
              updated_at: new Date().toISOString()
            };
            
            if (name === 'watercolor') {
              updateData.watercolor_image_url = uploadedUrl;
            } else if (name === 'pencil') {
              updateData.pencil_image_url = uploadedUrl;
            } else if (name === 'oil') {
              updateData.oil_image_url = uploadedUrl;
            }
            
            await supabase
              .from('property_searches')
              .update(updateData)
              .eq('id', propertySearchId);
              
            console.log(`Updated database with ${name} image for property:`, propertySearchId);
          } catch (dbError) {
            console.error(`Error updating database with ${name} image:`, dbError);
          }
        }
        
        // Update the frame context immediately when this image is ready
        if (name === 'watercolor') {
          setPropertyImage(uploadedUrl);
        }
        
        setStyleImages(prev => ({
          ...prev,
          [name as ImageStyle]: uploadedUrl
        }));
        
        setGeneratedImages(prev => ({
          ...prev,
          [name]: uploadedUrl
        }));
        
        return { style: name, url: uploadedUrl };
      } catch (error) {
        console.error(`Error generating ${name} style:`, error);
        return { style: name, url: null, error: error.message };
      }
    });

    // Wait for the first image to complete (or the only image in single mode)
    const firstCompleted = await Promise.race(imagePromises);
    
    if (!firstCompleted.url) {
      throw new Error(`Failed to generate first image: ${firstCompleted.error}`);
    }
    
    console.log(`First image completed: ${firstCompleted.style}`, firstCompleted.url);
    
    // Continue processing remaining images in the background (only for multi-image mode)
    if (!singleImageMode) {
      Promise.allSettled(imagePromises).then(results => {
        console.log('All image generations completed:', results);
        
        // Update status to completed when all images are done
        if (propertySearchId) {
          supabase
            .from('property_searches')
            .update({ 
              status: 'completed',
              updated_at: new Date().toISOString()
            })
            .eq('id', propertySearchId)
            .then(() => console.log('Updated property status to completed'))
            .catch(error => console.error('Error updating property status:', error));
        }
      });
    } else {
      // For single image mode, mark as completed immediately
      if (propertySearchId) {
        supabase
          .from('property_searches')
          .update({ 
            status: 'completed',
            updated_at: new Date().toISOString()
          })
          .eq('id', propertySearchId)
          .then(() => console.log('Updated property status to completed (single image mode)'))
          .catch(error => console.error('Error updating property status:', error));
      }
    }

    // After successful image generation, generate and save token
    try {
      const propertyData = await getPropertySearchById(propertySearchId || '');
      if (propertyData?.address && propertyData?.zip_code) {
        const token = await propertyTokenGenerator.generateToken(
          propertyData.address, 
          propertyData.zip_code
        );
        
        await propertyTokenGenerator.saveTokenToProperty(propertySearchId || '', token);
        console.log(`Generated property token: ${token} for property ${propertySearchId}`);
      }
    } catch (tokenError) {
      console.error('Error generating property token:', tokenError);
      // Don't fail the whole process if token generation fails
    }

    // Return the first completed image info for immediate navigation
    return {
      firstImageUrl: firstCompleted.url,
      firstImageType: firstCompleted.style as 'watercolor' | 'pencil' | 'oil'
    };
  } catch (error) {
    console.error('Error generating images:', error);
    setError(error instanceof Error ? error.message : 'Failed to generate AI images');
    throw error;
  } finally {
    setIsGeneratingAI(false);
  }
};

// Helper function to get property search by ID
const getPropertySearchById = async (propertySearchId: string) => {
  try {
    const { data, error } = await supabase
      .from('property_searches')
      .select('address, zip_code')
      .eq('id', propertySearchId)
      .single();
    
    if (error) {
      console.error('Error fetching property search:', error);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Error in getPropertySearchById:', error);
    return null;
  }
};

// Function to get or create client session ID
export const getOrCreateClientSessionId = (): string => {
  let clientSessionId;

  try {
    clientSessionId = localStorage.getItem(CLIENT_SESSION_ID_KEY);
  } catch (e) {
    console.warn('LocalStorage not available:', e);
  }
  
  if (!clientSessionId) {
    clientSessionId = uuidv4();
    
    try {
      localStorage.setItem(CLIENT_SESSION_ID_KEY, clientSessionId);
      
      if (supabase) {
        // Set client session ID in Supabase headers
        supabase.headers = {
          ...supabase.headers,
          'client-session-id': clientSessionId
        };
      }
    } catch (e) {
      console.warn('Could not save client session ID to localStorage:', e);
    }
  }
  
  return clientSessionId;
};

// Save property search to database
export const savePropertySearch = async (
  address: string,
  streetViewImage?: string | null,
  structuredAddress?: {
    address_line_1?: string | null;
    address_line_2?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
    zipCode?: string | null;  // Add support for camelCase version
    postal_code?: string | null; // Add support for Google Places API format
    country?: string | null;
  }
): Promise<string | null> => {
  try {
    // Ensure client session ID is properly set on the Supabase client
    refreshClientSessionId();
    
    console.log('Saving property search:', address);
    console.log('Structured address:', structuredAddress);
    
    // Get client session ID
    const clientSessionId = getOrCreateClientSessionId();
    console.log('Using client session ID:', clientSessionId);
    
    // Generate a UUID for the new property search
    const newId = crypto.randomUUID();
    console.log('Generated new UUID for property search:', newId);
    
    // Get zip code from any of the possible property names
    const zipCode = structuredAddress?.zip_code || 
                   structuredAddress?.zipCode || 
                   structuredAddress?.postal_code || 
                   null;
    
    // Create the search data
    const searchData = {
      id: newId,
      address: address.trim(),
      master_image_url: streetViewImage || null,
      status: 'pending',
      address_line_1: structuredAddress?.address_line_1 || null,
      address_line_2: structuredAddress?.address_line_2 || null,
      city: structuredAddress?.city || null,
      state: structuredAddress?.state || null,
      zip_code: zipCode,
      country: structuredAddress?.country || 'US',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      client_session_id: clientSessionId
    };

    console.log('Creating property search record:', searchData);
    
    // Set the client session ID in the headers
    if (supabase?.rest?.headers) {
      supabase.rest.headers['client-session-id'] = clientSessionId;
    }
    
    // Insert the property search record
    const { data, error } = await supabase 
      .from('property_searches')
      .insert(searchData)
      .select('id')
      .single();
    
    if (error) {
      console.error('Error inserting property search:', error);
      console.error('Error details:', error.details, error.message);
      return null;
    }
    
    if (!data?.id) {
      console.error('No ID returned from insert operation');
      return null;
    }
    
    console.log('Successfully inserted property search with ID:', data.id);

    return data.id;
  } catch (error) {
    console.error('Error saving property search:', error);
    return null;
  }
};

// Update property search with analysis results
export const updatePropertySearchAnalysis = async (
  id: string,
  analysisData: {
    contactSheetUrl?: string | null;
    selectedImageUrl?: string | null;
    suitabilityScore?: number;
    confidenceLevel?: string;
  }
): Promise<boolean> => {
  try {
    // Skip update for null or temporary IDs
    if (!id || id.startsWith('temp-')) {
      console.log('Skipping database update for invalid ID:', id);
      return false;
    }
    
    console.log('Updating property search analysis:', { id, ...analysisData });
    
    const updateData: Record<string, any> = {
      updated_at: new Date().toISOString()
    };
    
    if (analysisData.contactSheetUrl) {
      updateData.contact_sheet_url = analysisData.contactSheetUrl;
    }
    
    if (analysisData.selectedImageUrl) {
      updateData.selected_image_url = analysisData.selectedImageUrl;
    }
    
    if (analysisData.suitabilityScore !== undefined) {
      updateData.suitability_score = analysisData.suitabilityScore;
    }
    
    if (analysisData.confidenceLevel) {
      updateData.confidence_score = analysisData.confidenceLevel === 'HIGH' ? 0.9 : 
                                   analysisData.confidenceLevel === 'MEDIUM' ? 0.6 : 0.3;
    }
    
    console.log('Updating property_searches record with ID:', id);
    console.log('Update data:', updateData);
    
    const { data: updateResult, error } = await supabase
      .from('property_searches')
      .update(updateData) 
      .eq('id', id) 
      .select();
    
    if (error) {
      console.error('Error updating property search analysis:', error);
      console.log('Update skipped for property search analysis');
      return false;
    }
    console.log('Property search analysis updated successfully:', updateResult);
    return true;
  } catch (error) {
    console.error('Error updating property search analysis:', error);
    return false;
  }
};

// Update property search with AI generated images
export const updatePropertySearchImages = async (
  id: string,
  imageData: {
    pencilUrl?: string | null;
    oilUrl?: string | null;
    watercolorUrl?: string | null;
    charcoalUrl?: string | null;
    chalkUrl?: string | null;
  }
): Promise<boolean> => {
  try {
    // Skip update for null or temporary IDs
    if (!id || id.startsWith('temp-')) {
      console.log('Skipping database update for invalid ID:', id);
      return false;
    }

    console.log('Updating property search images:', { id, ...imageData });
    
    const updateData: Record<string, any> = {
      status: 'completed',
      updated_at: new Date().toISOString()
    };
    
    if (imageData.pencilUrl) {
      updateData.pencil_image_url = imageData.pencilUrl;
    }
    
    if (imageData.oilUrl) {
      updateData.oil_image_url = imageData.oilUrl;
    }
    
    if (imageData.watercolorUrl) {
      updateData.watercolor_image_url = imageData.watercolorUrl;
    }
    
    if (imageData.charcoalUrl) {
      updateData.charcoal_image_url = imageData.charcoalUrl;
    }
    
    if (imageData.chalkUrl) {
      updateData.chalk_image_url = imageData.chalkUrl;
    }
    
    console.log('Updating property_searches record with ID:', id);
    console.log('Update data:', updateData);
    
    const { data: updateResult, error } = await supabase
      .from('property_searches')
      .update(updateData)
      .eq('id', id)
      .select();
      
    if (error) {
      console.error('Error updating property search images:', error);
      console.log('Update skipped for property search images');
      return false;
    }
    
    console.log('Property search images updated successfully:', updateResult);
    return true;
  } catch (error) {
    console.error('Error updating property search images:', error);
    return false;
  }
};

// Update property search with upscaled image URL
export const updatePropertySearchUpscaledImage = async (
  id: string,
  upscaledImageUrl: string | null,
  clientSessionId?: string
): Promise<boolean> => {
  try {
    // Skip update for null or temporary IDs
    if (!id || id.startsWith('temp-')) {
      console.log('Skipping database update for invalid ID:', id);
      return false;
    }

    console.log('Updating property search with upscaled image URL:', { id, upscaledImageUrl });

    const updateData: Partial<PropertySearch> = {
      updated_at: new Date().toISOString()
    };

    if (upscaledImageUrl) {
      updateData.upscaled_image_url = upscaledImageUrl;
      updateData.client_session_id = clientSessionId || getOrCreateClientSessionId();
    } else {
      updateData.upscaled_image_url = null; // Allow setting to null if needed
    }

    console.log('Updating property_searches record with ID:', id);
    console.log('Update data:', updateData);

    const { data: updateResult, error } = await supabase
      .from('property_searches')
      .update(updateData)
      .eq('id', id)
      .select();

    if (error) {
      console.error('Error updating property search with upscaled image URL:', error);
      console.log('Update skipped for property search upscaled image URL');
      return false;
    }

    console.log('Property search upscaled image URL updated successfully:', updateResult);
    return true;
  } catch (error) {
    console.error('Error updating property search upscaled image URL:', error);
    return false;
  }
};

// Parse address components from a full address
export const parseAddressComponents = (address: string): {
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
} => {
  try {
    // This is a simple parser for US addresses, could be improved
    const components: {
      address_line_1?: string;
      address_line_2?: string;
      city?: string;
      state?: string;
      zipCode?: string;
      country?: string;
    } = {};
    
    // Example: "123 Main St, Anytown, CA 12345, USA"
    const parts = address.split(',').map(part => part.trim());
    
    if (parts.length >= 1) {
      // First part is the complete street address (number + street name)
      components.address_line_1 = parts[0];
    }
    
    if (parts.length >= 2) {
      // Second part is usually the city
      components.city = parts[1];
    }
    
    if (parts.length >= 3) {
      // Third part usually contains state and zip code
      const stateZipPart = parts[2];
      const stateZipMatch = stateZipPart.match(/^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
      
      if (stateZipMatch) {
        components.state = stateZipMatch[1];
        components.zipCode = stateZipMatch[2];
      } else {
        // If it doesn't match the pattern, just use it as the state
        components.state = stateZipPart;
      }
    }
    
    if (parts.length >= 4) {
      // Fourth part could be zip code if not extracted earlier
      const zipMatch = parts[3].match(/^(\d{5}(?:-\d{4})?)$/);
      if (zipMatch && !components.zipCode) {
        components.zipCode = zipMatch[1];
      }
      // Or it could be country
      else if (['USA', 'US', 'United States', 'United States of America'].includes(parts[3])) {
        components.country = 'US';
      } else {
        components.country = parts[3];
      }
    }
    
    // Default country to US if not specified
    if (!components.country) {
      components.country = 'US';
    }
    
    return components;
  } catch (error) {
    console.error('Error parsing address components:', error);
    return {};
  }
};

// Start image processing workflow
export const startImageProcessingWorkflow = async (
  address: string,
  setShowProcessing: (state: boolean) => void,
  setProcessingStep: (step: number) => void,
  setIsSubmitting: (state: boolean) => void,
  setError: (error: string | null) => void,
  setReceivedImage: (image: string | null) => void,
  setZillowImages: (images: string[]) => void,
  setWebImages: (images: Array<{ webUrl: string; imageUrl: string; uploadedUrl?: string }>) => void,
  existingAddressComponents?: {
    address_line_1?: string | null;
    address_line_2?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
    country?: string | null;
  }
): Promise<{propertyId: string; zillowMainURL?: string} | void> => {
  setShowProcessing(true);
  setProcessingStep(0);
  setIsSubmitting(true);
  setError(null);
  setReceivedImage(null);
  let propertyId: string | null = null;
  
  try {
    // First save the address to the property_searches table
    // Parse address components with error handling
    let addressComponents;
    const clientSessionId = getOrCreateClientSessionId();
    
    try {
      // If we have existing address components, use those, otherwise parse from address
      if (existingAddressComponents) {
        addressComponents = existingAddressComponents;
        console.log('Using existing address components:', addressComponents);
      } else {
        addressComponents = parseAddressComponents(address);
        console.log('Successfully parsed address components:', addressComponents);
      }
    } catch (parseError) {
      console.warn('Failed to parse address components, continuing with raw address:', parseError);
      // Use empty object as fallback to allow search to continue
      addressComponents = {};
    }
    
    // Save to database to get an ID      
    try {
       propertyId = await savePropertySearch(address, null, addressComponents);
       console.log('Saved property search with ID:', propertyId);
       
       if (!propertyId) {
         throw new Error('Failed to save property search to database');
       }
    } catch (dbError) {
      console.error('Error saving to database:', dbError);
      throw new Error('Failed to create property search record');
    }
    
    // Prepare webhook data
    const webhookData = {
      address,
      source: 'submit_button',
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };

    // Try to get Street View image
    const streetViewData = await getStreetViewImage(address);
    
    // Process and upload Street View image if available
    let receivedImageUrl = null;
    if (streetViewData.status === 'OK' && streetViewData.imageUrl) {
      const processedImage = await processImageForOpenAI(streetViewData.imageUrl);
      const uploadedUrl = await uploadBase64Image(processedImage);
      setReceivedImage(uploadedUrl);
      receivedImageUrl = uploadedUrl;
      
      // Update the property search with the street view image
      if (propertyId) {
        try {
          console.log('Updating property search with street view image:', uploadedUrl);
          
          const { data: updateResult, error: updateError } = await supabase
            .from('property_searches')
            .update({ 
              master_image_url: uploadedUrl,
              updated_at: new Date().toISOString()
            })
            .eq('id', propertyId)
            .select('id');
            
          if (updateError) {
            console.error('Error updating master image URL:', updateError);
            console.error('Update error details:', updateError.details, updateError.hint, updateError.code);
          } else {
            console.log('Successfully updated master image URL:', updateResult);
            // Analyze image and send to widget (non-blocking)
            analyzeImageAndNotifyWidget(uploadedUrl, 'address');
          }
        } catch (updateError) {
          console.error('Error updating master image URL:', updateError);
          // Continue with the flow even if update fails
        }
      }
    }
    
    // Call both webhooks in parallel
    const [zillowPromise, webImagesPromise] = await Promise.allSettled([
      // Primary image search function (replaces webhook and old zillow-image-search)
      fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/primary-image-search`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ address })
      }),
      // Google images function
      fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/google-images`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ address })
      })
    ]);
    
    // Handle Zillow webhook response
    let zillowImages: string[] = [];
    let zillowMainURL: string | undefined;
    
    console.log('🔍 Processing Zillow response...');
    console.log('Zillow promise status:', zillowPromise.status);
    
    try {
      if (zillowPromise.status === 'fulfilled' && zillowPromise.value) {
        const responseText = await zillowPromise.value.text();
        console.log('📥 Zillow response text:', responseText);
        let hasImages = false;

        if (responseText !== 'No Zillow Images') {
          try {
            const webhookData = JSON.parse(responseText);
            console.log('📊 Parsed Zillow data:', webhookData);
            
            // Extract Zillow Main URL if present
            if (webhookData?.ZillowMainUrl) {
              zillowMainURL = webhookData.ZillowMainUrl;
              console.log('✅ Extracted Zillow Main URL:', zillowMainURL);
            }
            
            // Handle ZillowUrls array
            if (webhookData?.ZillowUrls?.length) {
              const validUrls = webhookData.ZillowUrls.filter(url => url && typeof url === 'string');
              zillowImages = validUrls;
              hasImages = validUrls.length > 0;
              console.log(`✅ Found ${validUrls.length} valid Zillow URLs:`, validUrls);
            }
            
            // For backward compatibility - also check OriginalUrls
            if (webhookData?.OriginalUrls?.length) {
              const validUrls = webhookData.OriginalUrls.filter(url => url && typeof url === 'string');
              if (zillowImages.length === 0) {
                zillowImages = validUrls;
              }
              hasImages = hasImages || validUrls.length > 0;
              console.log('📎 Also checked OriginalUrls for backward compatibility');
            }
            
            console.log('🎯 About to call setZillowImages with:', zillowImages);
            setZillowImages(zillowImages);
            console.log('✅ setZillowImages called successfully');
          } catch (jsonError) {
            console.error('❌ Failed to parse Zillow response:', jsonError);
          }
        } else {
          console.log('ℹ️ Received "No Zillow Images" response');
        }
      } else {
        console.log('⚠️ Zillow promise not fulfilled or no value');
      }
    } catch (error) {
      console.error('❌ Error processing Zillow response:', error);
    }
    
    // Handle web images webhook response
    let webImages: Array<{ webUrl: string; imageUrl: string; uploadedUrl?: string }> = [];
    try {
      if (webImagesPromise.status === 'fulfilled' && webImagesPromise.value) {
        const responseText = await webImagesPromise.value.text();
        
        try {
          const webImagesData = JSON.parse(responseText);
          
          // Log any warnings from the google-images function
          if (webImagesData?.warning) {
            console.warn('⚠️ Google Images API warning:', webImagesData.warning);
            if (webImagesData.error_details) {
              console.warn('⚠️ Error details:', webImagesData.error_details);
            }
          }
          
          if (webImagesData?.WebUrls && Array.isArray(webImagesData.WebUrls)) {
            // Process each web image - convert and upload any WebP images
            const processedWebImages = await Promise.all(
              webImagesData.WebUrls
                .filter((url: any) => url && typeof url.webUrl === 'string' && typeof url.imageUrl === 'string')
                .map(async (url: any) => {
                  // Check if the image is WebP or needs conversion
                  const needsConversion = await isWebPImage(url.imageUrl);
                  
                  if (needsConversion) {
                    console.log(`Converting WebP image: ${url.imageUrl}`);
                    const uploadedUrl = await convertAndUploadImage(url.imageUrl);
                    return {
                      webUrl: url.webUrl,
                      imageUrl: url.imageUrl,
                      uploadedUrl: uploadedUrl
                    };
                  }
                  
                  // If already a JPEG or has an uploaded URL, use as is
                  return {
                    webUrl: url.webUrl,
                    imageUrl: url.imageUrl,
                    uploadedUrl: url.uploadedUrl
                  };
                })
            );
            
            // Filter out CORS-blocked images
            console.log('Filtering web images for CORS accessibility...');
            const accessibleWebImages = await filterAccessibleImages(processedWebImages);
            
            webImages = accessibleWebImages;
            setWebImages(webImages);
            
            // Log the filtering results
            const blockedCount = processedWebImages.length - accessibleWebImages.length;
            if (blockedCount > 0) {
              console.warn(`⚠️ Filtered out ${blockedCount} CORS-blocked web images`);
            }
            
            console.log(`✅ Successfully processed ${webImages.length} web images`);
          } else {
            console.log('📝 No web images found in response');
          }
        } catch (parseError) {
          console.warn('⚠️ Failed to parse web images response (continuing without web images):', parseError);
        }
      } else if (webImagesPromise.status === 'rejected') {
        console.warn('⚠️ Web images request was rejected (continuing without web images):', webImagesPromise.reason);
      } else {
        console.log('📝 Web images request returned no response (continuing without web images)');
      }
    } catch (error) {
      console.warn('⚠️ Error processing web images response (continuing without web images):', error);
    }
    
    return propertyId ? { propertyId, zillowMainURL } : null;
  } catch (error) {
    console.error('Error processing images:', error);
    throw error;
  } finally {
    console.log('🏁 startImageProcessingWorkflow completed');
    console.log('📊 Final state summary:');
    console.log('  - propertyId:', propertyId);
    console.log('  - Function completed successfully');
    setIsSubmitting(false);
  }
};

// Update property search with image source
export const updatePropertySearchImageSource = async (
  id: string,
  imageSource: 'address' | 'upload' | 'webcam'
): Promise<boolean> => {
  try {
    // Skip update for null or temporary IDs
    if (!id || id.startsWith('temp-')) {
      console.log('Skipping database update for invalid ID:', id);
      return false;
    }
    
    console.log('Updating property search image source:', { id, imageSource });
    
    const updateData = {
      image_source: imageSource,
      updated_at: new Date().toISOString()
    };
    
    const { data: updateResult, error } = await supabase
      .from('property_searches')
      .update(updateData) 
      .eq('id', id) 
      .select();
    
    if (error) {
      console.error('Error updating property search image source:', error);
      return false;
    }
    
    console.log('Property search image source updated successfully:', updateResult);
    return true;
  } catch (error) {
    console.error('Error updating property search image source:', error);
    return false;
  }
};

// Save property search specifically for image upload/webcam without address
export const savePropertySearchForImage = async (imageSource: 'upload' | 'webcam', imageUrl: string): Promise<string | null> => {
  try {
    const sessionId = getOrCreateClientSessionId();
    
    // Generate temporary token for image-based property
    const tempToken = await propertyTokenGenerator.generateTemporaryToken();
    
    console.log(`Creating property search for ${imageSource} with temp token:`, tempToken);
    
    const { data, error } = await supabase
      .from('property_searches')
      .insert([{
        id: uuidv4(),
        client_session_id: sessionId,
        image_source: imageSource,
        master_image_url: imageUrl,
        property_token: tempToken, // Save temporary token
        token_generated_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        // Remove hardcoded address data - let user enter their own
        address: null,
        address_line_1: null,
        city: null,
        state: null,
        zip_code: null,
        country: 'USA' // Changed from 'US' to 'USA'
      }])
      .select()
      .single();

    if (error) {
      console.error('Error saving property search for image:', error);
      return null;
    }

    console.log('✅ Property search created with token:', {
      id: data.id,
      tempToken,
      imageSource,
      sessionId
    });
    
    return data.id;
  } catch (error) {
    console.error('Error in savePropertySearchForImage:', error);
    return null;
  }
};

// Add persistent storage for generation tracking to survive HMR
const GENERATION_STORAGE_KEY = 'framemyhome_generation_in_progress';

// Helper to get persistent generation state
const getPersistentGenerationState = (): Record<string, number> => {
  try {
    const stored = sessionStorage.getItem(GENERATION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

// Helper to set persistent generation state
const setPersistentGenerationState = (state: Record<string, number>) => {
  try {
    sessionStorage.setItem(GENERATION_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Fallback if sessionStorage fails
  }
};

// Enhanced check for generation in progress (persistent + memory)
const isGenerationInProgress = (propertySearchId: string): boolean => {
  // Check memory map first
  if (generationInProgress.has(propertySearchId)) {
    return true;
  }
  
  // Check persistent storage
  const persistentState = getPersistentGenerationState();
  const timestamp = persistentState[propertySearchId];
  
  if (timestamp) {
    const age = Date.now() - timestamp;
    const maxAge = 10 * 60 * 1000; // 10 minutes
    
    if (age < maxAge) {
      // Still valid, restore to memory map
      generationInProgress.set(propertySearchId, true);
      return true;
    } else {
      // Expired, clean up
      delete persistentState[propertySearchId];
      setPersistentGenerationState(persistentState);
    }
  }
  
  return false;
};

// Enhanced set generation in progress (persistent + memory)
const setGenerationInProgressPersistent = (propertySearchId: string) => {
  // Set in memory
  generationInProgress.set(propertySearchId, true);
  
  // Set in persistent storage with timestamp
  const persistentState = getPersistentGenerationState();
  persistentState[propertySearchId] = Date.now();
  setPersistentGenerationState(persistentState);
  
  // Set cleanup timeout
  const timeoutId = setTimeout(() => {
    console.warn(`⚠️ Generation timeout reached for property: ${propertySearchId}`);
    clearGenerationInProgressPersistent(propertySearchId);
  }, 10 * 60 * 1000); // 10 minutes
  
  generationTimeouts.set(propertySearchId, timeoutId);
};

// Enhanced clear generation in progress (persistent + memory)
const clearGenerationInProgressPersistent = (propertySearchId: string) => {
  // Clear from memory
  generationInProgress.delete(propertySearchId);
  
  // Clear from persistent storage
  const persistentState = getPersistentGenerationState();
  delete persistentState[propertySearchId];
  setPersistentGenerationState(persistentState);
  
  // Clear timeout
  const timeoutId = generationTimeouts.get(propertySearchId);
  if (timeoutId) {
    clearTimeout(timeoutId);
    generationTimeouts.delete(propertySearchId);
  }
};

// Add a static map to track properties currently being processed
const generationInProgress = new Map<string, boolean>();
const generationTimeouts = new Map<string, number>();

// Function to generate missing AI styles for an existing property
export const generateMissingAIStyles = async (
  propertySearchId: string,
  masterImageUrl: string,
  existingStyles: {
    watercolor?: string | null;
    pencil?: string | null;
    oil?: string | null;
  },
  setStyleImages: (images: Record<ImageStyle, string | null>) => void,
  setPropertyImage: (image: string | null) => void,
  mode: 'home' | 'pet' = 'home'
): Promise<void> => {
  const logPrefix = `🎨 [${propertySearchId.slice(-8)}]`;
  console.log(`${logPrefix} generateMissingAIStyles called`);
  
  try {
    // PERSISTENT DUPLICATE PREVENTION: Check both memory and sessionStorage
    if (isGenerationInProgress(propertySearchId)) {
      console.log(`${logPrefix} 🚫 Generation already in progress (persistent check) - BLOCKING DUPLICATE CALL`);
      return;
    }
    
    // Set flag IMMEDIATELY using persistent system
    setGenerationInProgressPersistent(propertySearchId);
    console.log(`${logPrefix} 🔒 Marked generation as in progress (persistent + atomic)`);
    
    // Paranoid verification - check if multiple processes somehow got through
    const persistentState = getPersistentGenerationState();
    const memoryCount = Array.from(generationInProgress.keys()).filter(id => id === propertySearchId).length;
    const hasMultipleProcesses = memoryCount > 1 || Object.keys(persistentState).filter(id => id === propertySearchId).length > 1;
    
    if (hasMultipleProcesses) {
      console.error(`${logPrefix} 🚨 CRITICAL: Multiple processes detected - aborting this instance`);
      clearGenerationInProgressPersistent(propertySearchId);
      return;
    }
    
    console.log(`${logPrefix} Checking for missing AI styles for property:`, propertySearchId);
    
    // Get the property data to check for selected_image_url and current styles
    const { data: propertyData } = await supabase
      .from('property_searches')
      .select('selected_image_url, master_image_url, watercolor_image_url, pencil_image_url, oil_image_url')
      .eq('id', propertySearchId)
      .single();
    
    // Use selected_image_url if available, otherwise fallback to provided masterImageUrl
    const sourceImageUrl = propertyData?.selected_image_url || masterImageUrl;
    console.log(`${logPrefix} Using source image for AI generation:`, {
      usingSelectedImage: !!propertyData?.selected_image_url,
      sourceImageUrl
    });
    
    // Check current database state (may have been updated by concurrent calls)
    const currentStyles = {
      watercolor: propertyData?.watercolor_image_url,
      pencil: propertyData?.pencil_image_url,
      oil: propertyData?.oil_image_url
    };
    
    // Define all required styles
    const requiredStyles = ['watercolor', 'pencil', 'oil'];
    const missingStyles = requiredStyles.filter(style => !currentStyles[style]);
    
    if (missingStyles.length === 0) {
      console.log(`${logPrefix} All AI styles are already available (checked database)`);
      return;
    }
    
    console.log(`${logPrefix} Missing AI styles (after database check):`, missingStyles);
    
    // === AUTO-DETECT IMAGE CONTENT TYPE ===
    let detectedContentType: 'home' | 'pet' = mode;
    
    try {
      console.log(`${logPrefix} 🔍 Auto-detecting image content type...`);
      const analysisResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ imageUrl: sourceImageUrl })
      });

      if (analysisResponse.ok) {
        const analysisResult = await analysisResponse.json();
        const detectedType = analysisResult.imageType?.toLowerCase();
        
        console.log(`${logPrefix} ✅ Image analysis result:`, {
          detectedType,
          userMode: mode
        });
        
        // Simple rule: Is it a HOUSE? If NO → use pet prompts (works for anything)
        if (detectedType === 'home') {
          // It's a house/building → use home prompts (architectural focus)
          detectedContentType = 'home';
          if (mode === 'pet') {
            console.log(`${logPrefix} ⚠️ OVERRIDE: Image is a house/building. Using home prompts.`);
          }
        } else {
          // It's NOT a house (pet, person, object, etc.) → use pet prompts
          detectedContentType = 'pet';
          if (mode === 'home') {
            console.log(`${logPrefix} ⚠️ OVERRIDE: Image is NOT a house (${detectedType}). Using pet prompts.`);
          }
        }
      } else {
        console.warn(`${logPrefix} ⚠️ Image analysis failed, using user-selected mode:`, mode);
      }
    } catch (analysisError) {
      console.warn(`${logPrefix} ⚠️ Image analysis error:`, analysisError);
      console.log(`${logPrefix} Falling back to user-selected mode:`, mode);
    }
    
    console.log(`${logPrefix} 🎨 Final mode for prompts:`, detectedContentType);
    
    // Process the source image for OpenAI
    const processedImage = await processImageForOpenAI(sourceImageUrl);
    
    // Get custom prompts based on DETECTED content type
    const customPrompts = getCustomPrompts(detectedContentType);
    console.log(`${logPrefix} Using prompts for detected mode: ${detectedContentType}`);
    
    // Generate missing styles concurrently
    const missingStylePromises = missingStyles.map(async (styleName) => {
      try {
        console.log(`${logPrefix} Generating missing ${styleName} style...`);
        
        const prompt = customPrompts[styleName];
        console.log(`${logPrefix} Using prompt for ${styleName}:`, prompt.substring(0, 100) + '...');
        
        // OpenAI API call
        console.log(`${logPrefix} Calling OpenAI API for ${styleName}...`);
        const imageUrl = await editImageWithOpenAI(processedImage, prompt);
        console.log(`${logPrefix} OpenAI returned URL for ${styleName}:`, imageUrl ? 'SUCCESS' : 'FAILED');
        
        if (!imageUrl) {
          throw new Error(`OpenAI returned null/empty URL for ${styleName}`);
        }
        
        // Apply post-processing effects based on style
        let finalImageUrl = imageUrl;
        if (styleName === 'watercolor') {
          console.log(`${logPrefix} Applying 35% desaturation to watercolor image...`);
          finalImageUrl = await desaturateImage(imageUrl, 35);
          console.log(`${logPrefix} Desaturation completed for watercolor`);
        } else if (styleName === 'pencil') {
          console.log(`${logPrefix} Applying +20 lightness adjustment to pencil image...`);
          finalImageUrl = await adjustLightness(imageUrl, 20);
          console.log(`${logPrefix} Lightness adjustment completed for pencil`);
        }
        
        console.log(`${logPrefix} Generated ${styleName} style - Final URL length:`, finalImageUrl?.length || 0);
        
        if (!finalImageUrl) {
          throw new Error(`Post-processing failed for ${styleName} - finalImageUrl is null/empty`);
        }
        
        console.log(`${logPrefix} ✅ Successfully generated ${styleName} image`);
        
        // Update the React state immediately (optimistic update)
        setStyleImages((prev) => ({
          ...prev,
          [styleName]: finalImageUrl,
        }));
        
        console.log(`${logPrefix} ✅ React state updated for ${styleName}`);
        
        return { style: styleName, url: finalImageUrl, success: true };
      } catch (error) {
        console.error(`${logPrefix} ❌ Complete failure for ${styleName}:`, {
          error: error.message,
          stack: error.stack,
          styleName
        });
        return { style: styleName, error: error.message, success: false };
      }
    });
    
    const results = await Promise.all(missingStylePromises);
    console.log(`${logPrefix} ✅ Completed generating missing AI styles`);
    
    // Log summary of results
    const successful = results.filter(r => r.success);
    const failed = results.filter(r => !r.success);
    
    console.log(`${logPrefix} 📊 Generation summary: ${successful.length} successful, ${failed.length} failed`);
    successful.forEach(r => console.log(`${logPrefix}   ✅ ${r.style}: SUCCESS`));
    failed.forEach(r => console.log(`${logPrefix}   ❌ ${r.style}: ${r.error}`));
    
    if (successful.length === 0) {
      console.error(`${logPrefix} ❌ All AI style generations failed - no database update needed`);
      return;
    }
    
    // Batch update the database using the edge function (bypasses RLS)
    console.log(`${logPrefix} 📦 Updating database with ${successful.length} AI images via edge function...`);
    
    const styleUpdates: Record<string, string> = {};
    successful.forEach(result => {
      styleUpdates[result.style] = result.url;
    });
    
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-ai-images`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          propertyId: propertySearchId,
          styleUpdates: styleUpdates
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Edge function error: ${response.status} - ${errorText}`);
      }
      
      const updateResult = await response.json();
      
      if (!updateResult.success) {
        throw new Error(`Edge function failed: ${updateResult.error}`);
      }
      
      console.log(`${logPrefix} ✅ Database updated successfully via edge function`);
      console.log(`${logPrefix} 📊 Updated styles: [${updateResult.updatedStyles.join(', ')}]`);
      console.log(`${logPrefix} 🕐 Database timestamp: ${updateResult.timestamp}`);
      
      if (failed.length > 0) {
        console.warn(`${logPrefix} ⚠️ Some AI style generations failed - images may need to be regenerated on next visit`);
      } else {
        console.log(`${logPrefix} 🎉 All AI style generations completed successfully!`);
      }
      
    } catch (dbError) {
      console.error(`${logPrefix} ❌ Database update failed:`, dbError);
      console.warn(`${logPrefix} ⚠️ AI images generated but not saved to database - will regenerate on next visit`);
      
      // Don't throw here - we want to keep the optimistic UI updates even if DB fails
    }
  } catch (error) {
    console.error(`🎨 [${propertySearchId.slice(-8)}] ❌ Error in generateMissingAIStyles:`, error);
  } finally {
    // Always clear the generation flag when done (success or error) - persistent cleanup
    const logPrefix = `🎨 [${propertySearchId.slice(-8)}]`;
    console.log(`${logPrefix} 🔓 Clearing generation flag (persistent cleanup)`);
    
    // Use persistent cleanup that clears both memory and sessionStorage
    clearGenerationInProgressPersistent(propertySearchId);
  }
};

// Function to test if an image is accessible (no CORS issues)
export const testImageAccessibility = async (imageUrl: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    
    const timeout = setTimeout(() => {
      console.warn(`Image accessibility test timed out for: ${imageUrl}`);
      resolve(false);
    }, 5000); // 5 second timeout
    
    img.onload = () => {
      clearTimeout(timeout);
      console.log(`✅ Image accessible: ${imageUrl}`);
      resolve(true);
    };
    
    img.onerror = (error) => {
      clearTimeout(timeout);
      console.warn(`❌ Image not accessible (likely CORS): ${imageUrl}`, error);
      resolve(false);
    };
    
    // Add cache buster to avoid cached CORS issues
    img.src = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
  });
};

// Function to filter out inaccessible images
export const filterAccessibleImages = async (
  webImages: Array<{
    webUrl: string;
    imageUrl: string;
    uploadedUrl?: string;
  }>
): Promise<Array<{
  webUrl: string;
  imageUrl: string;
  uploadedUrl?: string;
}>> => {
  console.log(`Testing accessibility for ${webImages.length} web images...`);
  
  const accessibilityTests = await Promise.all(
    webImages.map(async (img) => {
      const isAccessible = await testImageAccessibility(img.imageUrl);
      return { ...img, isAccessible };
    })
  );
  
  const accessibleImages = accessibilityTests.filter(img => img.isAccessible);
  const blockedCount = webImages.length - accessibleImages.length;
  
  if (blockedCount > 0) {
    console.warn(`⚠️ ${blockedCount} images blocked by CORS and filtered out`);
  }
  
  console.log(`✅ ${accessibleImages.length} images are accessible`);
  return accessibleImages;
};